import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import Dashboard from './pages/Dashboard';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const AppContext = React.createContext();

function App() {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [provinces, setProvinces] = useState([]);

  const fetchProvinces = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/provinces`);
      setProvinces(response.data);
    } catch (error) {
      console.error('Failed to fetch provinces:', error);
    }
  }, []);

  const fetchDashboardData = useCallback(async (province = null) => {
    try {
      const params = province ? `?province=${province}` : '';
      const response = await axios.get(`${API}/dashboard${params}`);
      setDashboardData(response.data);
      setLastUpdated(new Date());
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load market data');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProvinces();
    fetchDashboardData(selectedProvince);
    
    // Auto-refresh every 15 minutes
    const interval = setInterval(() => {
      fetchDashboardData(selectedProvince);
      toast.success('Market data refreshed');
    }, 15 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchDashboardData, fetchProvinces, selectedProvince]);

  const refreshData = async () => {
    toast.info('Refreshing market data...');
    await fetchDashboardData(selectedProvince);
    toast.success('Data updated successfully');
  };

  const changeProvince = async (province) => {
    setLoading(true);
    setSelectedProvince(province);
    await fetchDashboardData(province);
    if (province) {
      const provinceName = provinces.find(p => p.code === province)?.name || province;
      toast.success(`Viewing ${provinceName} market data`);
    } else {
      toast.success('Viewing all of Canada');
    }
  };

  return (
    <AppContext.Provider value={{ 
      dashboardData, 
      loading, 
      lastUpdated, 
      refreshData,
      selectedProvince,
      setSelectedProvince: changeProvince,
      provinces
    }}>
      <div className="App min-h-screen bg-[#09090B]">
        <Toaster 
          position="bottom-right" 
          theme="dark"
          toastOptions={{
            style: {
              background: '#18181B',
              border: '1px solid #27272A',
              color: '#FFFFFF',
              zIndex: 40
            }
          }}
        />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="*" element={<Dashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AppContext.Provider>
  );
}

export default App;
